import React, { useState, useEffect, useCallback } from "react";
import clsx from "clsx";
import Drawer from "@material-ui/core/Drawer";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import RefreshIcon from "@material-ui/icons/Refresh";
import { makeStyles } from "@material-ui/core/styles";
import { format } from "date-fns";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { connectToSocket } from "../../services/socket";
import JsonTree from "../../components/JsonTree";
import RecordingPlayer from "../../components/RecordingPlayer";

const useStyles = makeStyles(theme => ({
  paper: { width: 720, maxWidth: "100vw", display: "flex", flexDirection: "column" },
  head: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: theme.spacing(1.5, 2),
    borderBottom: `1px solid ${theme.app.border}`,
  },
  title: { fontWeight: 700, flex: 1 },
  body: {
    display: "flex",
    flex: 1,
    minHeight: 0,
    [theme.breakpoints.down("sm")]: { flexDirection: "column" },
  },
  list: {
    width: 300,
    borderRight: `1px solid ${theme.app.border}`,
    overflowY: "auto",
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      borderRight: "none",
      borderBottom: `1px solid ${theme.app.border}`,
      maxHeight: "40vh",
      flexShrink: 0,
    },
  },
  detail: { flex: 1, overflowY: "auto", padding: theme.spacing(1.5) },
  run: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: theme.spacing(1.25, 1.5),
    cursor: "pointer",
    borderLeft: "3px solid transparent",
    borderBottom: `1px solid ${theme.app.border}`,
    "&:hover": { background: theme.app.selectedRow },
  },
  runActive: { background: theme.app.selectedRow, borderLeft: `3px solid ${theme.app.lime}` },
  dot: { width: 9, height: 9, borderRadius: "50%", flexShrink: 0 },
  ok: { background: "#1E7E34" },
  err: { background: "#B12A2A" },
  info: { background: theme.app.textTertiary },
  runMain: { flex: 1, minWidth: 0 },
  runLabel: { fontSize: 13, color: theme.app.dark, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  runMeta: { fontSize: 11, color: theme.app.textTertiary },
  step: {
    border: `1px solid ${theme.app.border}`,
    borderRadius: 8,
    marginBottom: theme.spacing(1),
    overflow: "hidden",
  },
  stepHead: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: theme.spacing(1, 1.25),
    cursor: "pointer",
    "&:hover": { background: theme.app.chatBg },
  },
  stepName: { flex: 1, fontSize: 13, fontWeight: 600, color: theme.app.dark },
  stepMsg: { fontSize: 12, color: theme.app.textTertiary, marginLeft: 26, marginBottom: 4 },
  stepTime: { fontSize: 11, color: theme.app.textTertiary },
  stepBody: { padding: theme.spacing(1, 1.25), borderTop: `1px solid ${theme.app.border}`, background: "#fff" },
  ioLabel: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: theme.app.textTertiary,
    margin: theme.spacing(1, 0, 0.5),
  },
  empty: { padding: theme.spacing(4, 2), textAlign: "center", color: theme.app.textTertiary },
}));

const dotClass = (classes, s) => (s === "success" ? classes.ok : s === "error" ? classes.err : classes.info);

const LiveEventsPanel = ({ open, onClose, appId }) => {
  const classes = useStyles();
  const [runs, setRuns] = useState([]);
  const [selected, setSelected] = useState(null); // runId
  const [chain, setChain] = useState([]);
  const [openStep, setOpenStep] = useState(null);

  const loadRuns = useCallback(async () => {
    if (!appId) return;
    try {
      const { data } = await api.get("/events/runs", { params: { applicationId: appId } });
      setRuns(data.runs || []);
    } catch (err) {
      toastError(err);
    }
  }, [appId]);

  const openRun = async run => {
    setSelected(run.runId);
    setOpenStep(null);
    try {
      const { data } = await api.get("/events", { params: { runId: run.runId } });
      setChain(data.events || []);
    } catch (err) {
      toastError(err);
    }
  };

  useEffect(() => {
    if (!open) return undefined;
    loadRuns();
    const socket = connectToSocket();
    socket.emit("joinRoom", "events");
    socket.on("event", p => {
      if (p.action === "create" && String(p.event.applicationId) === String(appId)) {
        loadRuns();
        // if the new event belongs to the open run, refresh the chain
        setSelected(sel => {
          if (sel && p.event.runId === sel) {
            api.get("/events", { params: { runId: sel } }).then(({ data }) => setChain(data.events || []));
          }
          return sel;
        });
      }
    });
    return () => {
      socket.emit("leaveRoom", "events");
      socket.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, appId]);

  return (
    <Drawer anchor="right" open={open} onClose={onClose} classes={{ paper: classes.paper }}>
      <div className={classes.head}>
        <Typography className={classes.title}>Live Events — execuções</Typography>
        <IconButton size="small" title="Atualizar" onClick={loadRuns}>
          <RefreshIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </div>
      <div className={classes.body}>
        <div className={classes.list}>
          {runs.length === 0 ? (
            <div className={classes.empty}>
              <Typography variant="body2">Nenhuma execução ainda.</Typography>
              <Typography variant="caption">Dispare o webhook para ver a cadeia aqui.</Typography>
            </div>
          ) : (
            runs.map(r => (
              <div
                key={r.runId}
                className={clsx(classes.run, selected === r.runId && classes.runActive)}
                onClick={() => openRun(r)}
              >
                <span className={clsx(classes.dot, dotClass(classes, r.status))} />
                <div className={classes.runMain}>
                  <div className={classes.runLabel}>{r.label}</div>
                  <div className={classes.runMeta}>
                    {format(new Date(r.startedAt), "dd/MM/yyyy HH:mm:ss")} · {r.count} passos
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
        <div className={classes.detail}>
          {!selected ? (
            <div className={classes.empty}>
              <Typography variant="body2">Selecione uma execução para ver a cadeia.</Typography>
            </div>
          ) : (
            chain.map(ev => {
              const d = ev.data || {};
              const hasInOut = d.input !== undefined || d.output !== undefined;
              return (
                <div key={ev.id} className={classes.step}>
                  <div className={classes.stepHead} onClick={() => setOpenStep(s => (s === ev.id ? null : ev.id))}>
                    <span className={clsx(classes.dot, dotClass(classes, ev.status))} />
                    <span className={classes.stepName}>{d.node || ev.type}</span>
                    <span className={classes.stepTime} title={format(new Date(ev.createdAt), "dd/MM/yyyy HH:mm:ss")}>{format(new Date(ev.createdAt), "dd/MM HH:mm:ss")}</span>
                  </div>
                  {ev.message && <div className={classes.stepMsg}>{ev.message}</div>}
                  {ev.type === "recording.saved" && d.callSid && (
                    <RecordingPlayer callSid={d.callSid} applicationId={appId} publicUrl={d.publicUrl} />
                  )}
                  {openStep === ev.id && (
                    <div className={classes.stepBody}>
                      {hasInOut ? (
                        <>
                          {d.input !== undefined && (
                            <>
                              <div className={classes.ioLabel}>Entrada (in)</div>
                              <JsonTree data={d.input} rootKey="in" />
                            </>
                          )}
                          {d.output !== undefined && (
                            <>
                              <div className={classes.ioLabel}>Saída (out)</div>
                              <JsonTree data={d.output} rootKey="out" />
                            </>
                          )}
                        </>
                      ) : (
                        <JsonTree data={d} rootKey={ev.type} />
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </Drawer>
  );
};

export default LiveEventsPanel;
